import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import DNAImage from 'assets/images/DNA_min.webp'
import FileUploader from 'components/fileUploader/FileUploader'
import StaticPageWrapper from 'components/staticPage/StaticPageWrapper'
import { useDataContext } from 'context/DataContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Tumor, ReportViewerData } from 'reportViewer'
import { useStaticStyles } from 'styles/shared.styles'
import { useEffect, useState, useCallback } from 'react'

const possibleFileExtensions = ['json']

const NotFound = () => {
    const {
        wrapper,
        imageWrapper,
        descriptionWrapper,
        fileUpload,
        imageStyle
    } = useStaticStyles()

    const { setCompounds, setTargets, setDrivers, setTumor, setFileName } = useDataContext()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [isLoading, setIsLoading] = useState(false)

    const processData = useCallback((data: ReportViewerData | null, fileName?: string) => {
        if (fileName) {
            setFileName(fileName)
        }
        const molecularProfileAnalysis = data?.molecularProfileAnalysis

        const compounds = data?.compounds || []
        const targets = molecularProfileAnalysis?.targets || []
        const drivers = molecularProfileAnalysis?.molecularBiomarkers || []
        const tumor = data?.tumor || ({} as Tumor)

        setCompounds(compounds)
        setTargets(targets)
        setDrivers(drivers)
        setTumor(tumor)

        navigate('/compounds')
    }, [setFileName, setCompounds, setTargets, setDrivers, setTumor, navigate])

    const handleFileChange = (
        _file: File[] | null,
        data: ReportViewerData | null
    ) => {
        if (_file?.[0]) {
            processData(data, _file[0].name)
        } else {
            processData(data)
        }
    }

    useEffect(() => {
        const dataUrlParam = searchParams.get('data')

        if (!dataUrlParam) {
            return
        }

        let dataUrl: string
        try {
            dataUrl = atob(dataUrlParam)
        } catch (e) { return; }

        setIsLoading(true)
        fetch(dataUrl)
                .then(async (response) => {
                    if (!response.ok) {
                        setIsLoading(false)
                        return
                    }

                    const jsonData: ReportViewerData = await response.json()
                    const urlParts = dataUrl.split('/')
                    const fileName = urlParts[urlParts.length - 1].split('?')[0] || 'report.json'
                    processData(jsonData, fileName)
                })
                .catch(() => {
                    setIsLoading(false)
                })
    }, [searchParams, processData])

    return (
        <StaticPageWrapper title="Upload JSON" withContact={true}>
            <Box className={wrapper}>
                <Box>
                    <Box className={imageWrapper} sx={{ mt: 4 }}>
                        <img alt="dna" src={DNAImage} className={imageStyle} />
                    </Box>
                    <Box className={descriptionWrapper}>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <CircularProgress />

                            </Box>
                        ) : (
                            <FileUploader
                                label={'Drop files or click to browse'}
                                name="molecularProfileFiles"
                                types={possibleFileExtensions}
                                classes={fileUpload}
                                onChange={handleFileChange}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        </StaticPageWrapper>
    )
}

export default NotFound
